import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { ActionPlan } from '../types/types';

type ActionPlanItemProgressRow = {
  plan_id: string;
  is_completed: boolean | null;
};

export const useActionPlans = (supplierId?: string) => {
  const [plans, setPlans] = useState<ActionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    if (!supplierId) {
      setPlans([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: queryError } = await supabase
      .from('action_plans')
      .select(
        'id, supplier_id, assessment_ids, overall_score_at_generation, status, created_at, updated_at, assigned_by',
      )
      .eq('supplier_id', supplierId)
      .order('created_at', { ascending: false });

    if (queryError) {
      console.error('Error fetching action plans:', queryError);
      setError(queryError.message);
      setPlans([]);
    } else {
      const basePlans = (data ?? []) as ActionPlan[];

      if (basePlans.length === 0) {
        setPlans([]);
        setLoading(false);
        return;
      }

      const planIds = basePlans.map((plan) => plan.id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('action_plan_items')
        .select('plan_id, is_completed')
        .in('plan_id', planIds);

      if (itemsError) {
        console.error('Error fetching action plan items:', itemsError);
        setError(itemsError.message);
        setPlans(
          basePlans.map((plan) => ({
            ...plan,
            total_items_count: 0,
            completed_items_count: 0,
          })),
        );
        setLoading(false);
        return;
      }

      const progressByPlanId = (
        (itemsData ?? []) as ActionPlanItemProgressRow[]
      ).reduce<Record<string, { total: number; completed: number }>>(
        (accumulator, item) => {
          const current = accumulator[item.plan_id] ?? {
            total: 0,
            completed: 0,
          };

          current.total += 1;
          if (item.is_completed) {
            current.completed += 1;
          }

          accumulator[item.plan_id] = current;
          return accumulator;
        },
        {},
      );

      setPlans(
        basePlans.map((plan) => ({
          ...plan,
          total_items_count: progressByPlanId[plan.id]?.total ?? 0,
          completed_items_count: progressByPlanId[plan.id]?.completed ?? 0,
        })),
      );
    }

    setLoading(false);
  }, [supplierId]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    loading,
    error,
    refetch: fetchPlans,
  };
};
